import React, { useEffect, useState } from "react";
import { useParams, Link as RouterLink } from "react-router-dom";
import {
  Container,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Alert,
  Collapse,
  Box,
  Link,
  IconButton,
  FormControl,
  Select,
  MenuItem,
  Chip,
  TextField,
  Tooltip,
  Checkbox,
  FormControlLabel,
  Badge,
  Snackbar,
} from "@mui/material";
import { SelectChangeEvent } from "@mui/material/Select";
import {
  KeyboardArrowDown,
  KeyboardArrowUp,
  Save,
  PersonAdd,
} from "@mui/icons-material";
import {
  getLectureGCThreads,
  setGCThreadStatus,
  setGCThreadComment,
  setGCThreadReviewer,
  setGCThreadMark,
  getRagEligibleNotebooks,
} from "../services/api";
import { useAuth } from "../contexts/AuthContext";
import RagButton from "../components/RagButton";
import { Notebook } from "../types";

type Message = {
  author: string;
  author_gc_id: number;
  text: string;
  notebooks: Notebook[];
};
type GCThread = {
  status: "not_started" | "done" | "declined" | string;
  reviewer: string | null;
  comment: string | null;
  messages: Message[];
  mark: number | null;
  gc_lesson_id: number;
  gc_thread_id: number;
  thread_id: number;
};
type GCThreadsResponse = { threads: GCThread[]; reviewers: string[] };

const getStatusColor = (status: string) => {
  switch (status) {
    case "not_started":
      return "warning";
    case "done":
      return "success";
    case "declined":
      return "error";
    default:
      return "default";
  }
};

interface IRowProps {
  thread: GCThread;
  index: number;
  reviewers: string[];
  ragEligibleNotebooks: number[];
  displayStatusMessage: (message: string) => void;
  removeEligibilityForRag: (id: string) => void;
  setError: (error: string) => void;
  updateNotebookRagStatus: (notebookId: number, status: string) => void;
}

const Row: React.FC<IRowProps> = ({
  thread,
  index,
  reviewers,
  ragEligibleNotebooks,
  displayStatusMessage,
  removeEligibilityForRag,
  setError,
  updateNotebookRagStatus,
}) => {
  const [open, setOpen] = useState(false);
  const [status, setStatus] = useState<string>(thread.status);
  const [comment, setComment] = useState<string>(thread.comment || "");
  const [savingComment, setSavingComment] = useState(false);
  const [selectedReviewer, setSelectedReviewer] = useState<string | null>(
    thread.reviewer
  );
  const [mark, setMark] = useState<number | null>(thread.mark);
  const [savingMark, setSavingMark] = useState(false);
  const firstMsg = thread.messages[0];

  const hasMultiple = thread.messages.length > 1;

  const handleStatusChange = async (e: SelectChangeEvent<string>) => {
    const newStatus = e.target.value;
    setStatus(newStatus);
    try {
      await setGCThreadStatus(thread.thread_id, newStatus);
    } catch {
      setStatus(thread.status);
    }
  };

  const handleSaveComment = async () => {
    setSavingComment(true);
    try {
      await setGCThreadComment(thread.thread_id, comment);
    } finally {
      setSavingComment(false);
    }
  };

  const assignMe = async () => {
    try {
      const res = await setGCThreadReviewer(thread.thread_id, null);
      setSelectedReviewer(res.reviewer);
    } catch {}
  };

  const changeReviewer = async (e: SelectChangeEvent<string>) => {
    try {
      const res = await setGCThreadReviewer(thread.thread_id, e.target.value);
      setSelectedReviewer(res.reviewer);
    } catch {}
  };

  const handleMarkChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setMark(value === "" ? null : Number(value));
  };

  const saveMark = async () => {
    setSavingMark(true);
    try {
      const res = await setGCThreadMark(thread.thread_id, mark);
      setMark(res.mark);
    } finally {
      setSavingMark(false);
    }
  };

  const renderNotebookTitle = (nb: Notebook) => {
    if (nb.id === null) {
      return (
        <Typography variant="body2" color="text.secondary">
          {nb.name}
        </Typography>
      );
    }

    return (
      <>
        <Link
          component={RouterLink}
          to={`/notebooks/${nb.id}`}
          target="_blank"
          rel="noopener noreferrer"
          underline="hover"
          variant="body2"
        >
          {nb.name}
        </Link>
        <RagButton
          notebook={nb}
          onChangeRagStatus={(status) => updateNotebookRagStatus(nb.id, status)}
          isEligibleForRag={ragEligibleNotebooks.includes(nb.id)}
          onError={setError}
          removeEligibilityForRag={() =>
            removeEligibilityForRag(nb.id.toString())
          }
          displayRagSuccessMessage={displayStatusMessage}
        />
      </>
    );
  };

  return (
    <>
      <TableRow>
        <TableCell>
          <Box display="flex" flexDirection="column" alignItems="center">
            <Typography>{index}</Typography>
            {hasMultiple ? (
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  setOpen((o) => !o);
                }}
              >
                {open ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
              </IconButton>
            ) : (
              <Box height={24} />
            )}
          </Box>
        </TableCell>
        <TableCell>{firstMsg.author || "-"}</TableCell>
        <TableCell>{firstMsg.text || "-"}</TableCell>
        <TableCell>
          <Box display="flex" flexDirection="column">
            {firstMsg.notebooks.map((nb, idxNb) => {
              const commentCount = nb.nComments ?? 0;
              return (
                <Box key={`${thread.thread_id}-${idxNb}-${nb.name}`} mb={1}>
                  <Box display="flex" alignItems="center" gap={1}>
                    {renderNotebookTitle(nb)}
                    <Tooltip title="Количество комментариев">
                      <Badge
                        badgeContent={commentCount}
                        color="primary"
                        sx={{ ml: 2 }}
                      />
                    </Tooltip>
                  </Box>
                  {nb.shareToken && (
                    <Box mt={0.5}>
                      <Typography variant="caption">Студенту:</Typography>{" "}
                      <Link
                        component={RouterLink}
                        to={`/share/${nb.shareToken}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        underline="hover"
                        variant="caption"
                      >
                        Ноутбук
                      </Link>
                      {" | "}
                      <Link
                        component={RouterLink}
                        to={`/review/${nb.shareToken}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        underline="hover"
                        variant="caption"
                      >
                        Ревью
                      </Link>
                    </Box>
                  )}
                </Box>
              );
            })}
          </Box>
        </TableCell>
        <TableCell>
          <Box display="flex" alignItems="center" gap={0.5}>
            <FormControl variant="standard" size="small">
              <Select
                disableUnderline
                value={selectedReviewer || ""}
                onChange={changeReviewer}
                displayEmpty
                renderValue={(val) => val || "нет"}
              >
                <MenuItem value="" disabled>
                  <em>не назначено</em>
                </MenuItem>
                {reviewers.map((r) => (
                  <MenuItem key={r} value={r}>
                    {r}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Tooltip title="Назначить меня">
              <IconButton size="small" onClick={assignMe}>
                <PersonAdd fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        </TableCell>
        <TableCell>
          <Box display="flex" flexDirection="column" gap={0.5}>
            <TextField
              variant="standard"
              multiline
              fullWidth
              value={comment}
              onClick={(e) => e.stopPropagation()}
              onChange={(e) => {
                e.stopPropagation();
                setComment(e.target.value);
              }}
            />
            <Tooltip title="Сохранить комментарий">
              <span>
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleSaveComment();
                  }}
                  disabled={savingComment}
                >
                  <Save fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          </Box>
        </TableCell>
        <TableCell>
          <TextField
            variant="standard"
            type="number"
            size="small"
            value={mark != null ? mark : ""}
            onChange={handleMarkChange}
            onBlur={saveMark}
            disabled={savingMark}
            inputProps={{
              min: 0,
              onWheel: (e: React.WheelEvent<HTMLInputElement>) => {
                e.preventDefault();
              },
              onKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => {
                if (e.key === "ArrowUp" || e.key === "ArrowDown") {
                  e.preventDefault();
                }
              },
            }}
            sx={{
              "input::-webkit-outer-spin-button": {
                WebkitAppearance: "none",
                margin: 0,
              },
              "input::-webkit-inner-spin-button": {
                WebkitAppearance: "none",
                margin: 0,
              },
              "input[type=number]": { MozAppearance: "textfield" },
            }}
          />
        </TableCell>
        <TableCell>
          <FormControl variant="standard" size="small">
            <Select
              disableUnderline
              IconComponent={() => null}
              value={status}
              onChange={handleStatusChange}
              renderValue={(val) => (
                <Chip
                  label={val as string}
                  color={getStatusColor(val as string)}
                  size="small"
                />
              )}
            >
              <MenuItem value="not_started">
                <Chip label="not_started" color="warning" size="small" />
              </MenuItem>
              <MenuItem value="done">
                <Chip label="done" color="success" size="small" />
              </MenuItem>
              <MenuItem value="declined">
                <Chip label="declined" color="error" size="small" />
              </MenuItem>
            </Select>
          </FormControl>
        </TableCell>
      </TableRow>
      {hasMultiple && (
        <TableRow>
          <TableCell colSpan={8} sx={{ p: 0 }}>
            <Collapse in={open} timeout="auto" unmountOnExit>
              <Box m={2}>
                <Typography variant="subtitle1" gutterBottom>
                  Все сообщения в треде
                </Typography>
                {thread.messages.map((msg, i) => (
                  <Box
                    key={i}
                    mb={2}
                    p={1}
                    border="1px solid #eee"
                    borderRadius={1}
                  >
                    <Typography variant="subtitle2">
                      {msg.author} (GC ID: {msg.author_gc_id})
                    </Typography>
                    <Typography
                      component="pre"
                      sx={{
                        whiteSpace: "pre-wrap",
                        wordBreak: "breaking",
                        my: 1,
                      }}
                    >
                      {msg.text || "<без текста>"}
                    </Typography>
                    {msg.notebooks.length > 0 && (
                      <Box>
                        <Typography variant="body2">Файлы:</Typography>
                        {msg.notebooks.map((nb, idxMsgNb) => {
                          const count = nb.nComments ?? 0;
                          return (
                            <Box
                              key={`${thread.thread_id}-${msg.author_gc_id}-file-${idxMsgNb}-${nb.name}`}
                              mb={1}
                            >
                              <Box display="flex" alignItems="center" gap={1}>
                                {renderNotebookTitle(nb)}
                                <Tooltip title="Количество комментариев">
                                  <Badge
                                    badgeContent={count}
                                    color="primary"
                                    sx={{ ml: 2 }}
                                  />
                                </Tooltip>
                              </Box>

                              {nb.shareToken && (
                                <Box mt={0.5}>
                                  <Typography variant="caption">
                                    Предпросмотр ревью:
                                  </Typography>{" "}
                                  <Link
                                    component={RouterLink}
                                    to={`/share/${nb.shareToken}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    underline="hover"
                                    variant="caption"
                                  >
                                    Ноутбук
                                  </Link>
                                  {" | "}
                                  <Link
                                    component={RouterLink}
                                    to={`/review/${nb.shareToken}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    underline="hover"
                                    variant="caption"
                                  >
                                    Ревью
                                  </Link>
                                </Box>
                              )}
                            </Box>
                          );
                        })}
                      </Box>
                    )}
                  </Box>
                ))}
              </Box>
            </Collapse>
          </TableCell>
        </TableRow>
      )}
    </>
  );
};

export const LectureGCThreadsPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const lectureId = Number(id);
  const { user } = useAuth();
  const [threads, setThreads] = useState<GCThread[] | null>(null);
  const [reviewers, setReviewers] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [onlyMine, setOnlyMine] = useState(false);
  const [statusFilter, setStatusFilter] = useState<string>("");
  const [toast, setToast] = useState<string | null>(null);
  const [ragEligibleNotebooks, setRagEligibleNotebooks] = useState<number[]>(
    []
  );

  useEffect(() => {
    if (!lectureId) return;
    (async () => {
      try {
        const data: GCThreadsResponse = await getLectureGCThreads(lectureId);
        setThreads(data.threads);
        setReviewers(data.reviewers);
      } catch (err: any) {
        setError(err.message || "Не удалось загрузить треды");
      } finally {
        setLoading(false);
      }
    })();
  }, [lectureId]);

  const updateNotebookRagStatus = (notebookId: number, status: string) => {
    setThreads((prev) => {
      if (!prev) return null;
      return prev.map((thread) => {
        return {
          ...thread,
          messages: thread.messages.map((msg) => {
            return {
              ...msg,
              notebooks: msg.notebooks.map((nb) => {
                if (nb.id === notebookId) {
                  return { ...nb, rag_status: status };
                }
                return nb;
              }),
            };
          }),
        };
      });
    });
  };

  useEffect(() => {
    fetchRagEligibleNotebooks();
  }, []);

  const fetchRagEligibleNotebooks = async () => {
    try {
      const eligibleNotebooks = await getRagEligibleNotebooks(lectureId);
      setRagEligibleNotebooks(eligibleNotebooks);
    } catch (err) {
      console.error("Ошибка при загрузке списка ноутбуков для RAG:", err);
    }
  };

  const filtered = threads
    ?.filter((t) => !onlyMine || t.reviewer === user?.username)
    .filter((t) => !statusFilter || t.status === statusFilter);

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", my: 4 }}>
          <CircularProgress />
        </Box>
      ) : error ? (
        <Alert severity="error">{error}</Alert>
      ) : (
        <>
          <Box display="flex" alignItems="center" gap={2} mb={2}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={onlyMine}
                  onChange={(e) => setOnlyMine(e.target.checked)}
                />
              }
              label="Назначенные мне"
            />
            <FormControl variant="standard" size="small">
              <Select
                disableUnderline
                value={statusFilter}
                onChange={(e: SelectChangeEvent<string>) =>
                  setStatusFilter(e.target.value)
                }
                displayEmpty
                renderValue={(val) => val || "Все статусы"}
              >
                <MenuItem value="">
                  <em>Все статусы</em>
                </MenuItem>
                <MenuItem value="not_started">not_started</MenuItem>
                <MenuItem value="done">done</MenuItem>
                <MenuItem value="declined">declined</MenuItem>
              </Select>
            </FormControl>
          </Box>
          <TableContainer
            component={Paper}
            sx={{
              "& .MuiTableCell-root": {
                px: 1,
                py: 0.5,
              },
            }}
          >
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>№</TableCell>
                  <TableCell>Автор</TableCell>
                  <TableCell>Текст</TableCell>
                  <TableCell>Файлы</TableCell>
                  <TableCell>Ревьюер</TableCell>
                  <TableCell>Комментарий</TableCell>
                  <TableCell>Оценка</TableCell>
                  <TableCell>Статус</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filtered?.map((t, idx) => (
                  <Row
                    key={t.thread_id}
                    thread={t}
                    index={idx + 1}
                    reviewers={reviewers}
                    setError={setError}
                    ragEligibleNotebooks={ragEligibleNotebooks}
                    removeEligibilityForRag={(id) =>
                      setRagEligibleNotebooks((p) =>
                        p.filter((nbId) => nbId !== Number(id))
                      )
                    }
                    updateNotebookRagStatus={updateNotebookRagStatus}
                    displayStatusMessage={setToast}
                  />
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}
      {error && (
        <Alert severity="error" onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      <Snackbar
        open={!!toast}
        autoHideDuration={3000}
        onClose={() => setToast(null)}
        message={toast}
      />
    </Container>
  );
};
